import React, { useContext } from 'react';
import { styled } from '@compiled/react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import Spinner from '@atlaskit/spinner/spinner';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';

import { TEMPLATE_PREVIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';

import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';

import { hasCustomPreview, type TemplatePreviewTemplate } from './templatePreviewHelpers.tsx';
import { PreviewRenderer } from './PreviewRenderer/index.ts';
import { LoadingError } from './PreviewMessage/index.ts';
import type { TemplatePreviewTemplateBodiesQuery_templateBodies_PaginatedTemplateBodyList_nodes_TemplateBody as TemplateBody } from './__types__/TemplatePreviewTemplateBodiesQuery.ts';

type PreviewProps = {
	templateBody?: TemplateBody;
	isLoading?: boolean;
	error?: ApolloErrorInterop;
	template: TemplatePreviewTemplate;
	isScrollable: boolean;
	marginBottom: number;
	fabricDataProviders: ProviderFactory;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CenteredContainer = styled.div({
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const Preview = ({
	templateBody,
	isLoading,
	error,
	template,
	isScrollable,
	marginBottom,
	fabricDataProviders,
}: PreviewProps): React.JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (isLoading && !hasCustomPreview(template)) {
		return (
			<CenteredContainer>
				<Spinner size="medium" testId="template-preview-spinner" />
			</CenteredContainer>
		);
	}
	if (error && !hasCustomPreview(template)) {
		if (isUnauthorizedError(error) || isNotFoundError(error)) {
			experienceTracker.succeed({
				name: TEMPLATE_PREVIEW_EXPERIENCE,
			});
		} else {
			stopExperienceOnError(TEMPLATE_PREVIEW_EXPERIENCE, error);
		}

		return (
			<ErrorDisplay error={error}>
				<LoadingError error={error} />
			</ErrorDisplay>
		);
	}

	return (
		<PreviewRenderer
			templateBody={templateBody}
			error={error}
			template={template}
			isScrollable={isScrollable}
			marginBottom={marginBottom}
			fabricDataProviders={fabricDataProviders}
		/>
	);
};
